import React, { Fragment, useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminSidebar from './navigation/AdminSidebar';
import { Outlet, useNavigate } from 'react-router-dom';
import { FaBars, FaBell, FaSignOutAlt, FaShoppingBag, FaTimesCircle, FaCheckCircle } from 'react-icons/fa';
import chimeSound from './audio/notification.wav';


function LandingPageAdmin() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/notifications?limit=50`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    } catch (e) {
      console.error("Error fetching notifications:", e);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/notifications/mark-read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      fetchNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  const handleNotificationClick = async (notif) => {
    setNotifDropdownOpen(false);
    if (!notif.read) {
      try {
        const token = localStorage.getItem('authToken');
        await fetch(`${API_BASE_URL}/notifications/mark-read`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ ids: [notif._id] })
        });
      } catch (e) {
        console.error(e);
      }
    }
    const orderId = notif.orderId?._id || notif.orderId;
    if (orderId) {
      navigate(`/admin/orders-details/${orderId}`);
    } else {
      fetchNotifications();
    }
  };

  const playChime = () => {
    const audio = new Audio(chimeSound);
    audio.play().catch(e => console.log('Audio blocked:', e));
  };

  const formatTimeShort = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setNotifDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    fetchNotifications();

    const eventSource = new EventSource(`${API_BASE_URL}/order-stream`);
    
    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'notification') {
          const newNotif = payload.data;
          setNotifications(prev => [newNotif, ...prev].slice(0, 50));
          setUnreadCount(c => c + 1);
          playChime();
          toast(
            <div style={{ padding: '2px' }}>
              <strong className="d-block" style={{ fontSize: '0.85rem', color: '#0f172a' }}>{newNotif.title}</strong>
              <span style={{ fontSize: '0.78rem', color: '#475569' }}>{newNotif.message}</span>
            </div>,
            {
              position: 'top-right',
              autoClose: 6000,
              hideProgressBar: false,
              closeOnClick: true,
              style: { background: '#ffffff', borderLeft: '4px solid #ed3237', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }
            }
          );
        }
      } catch (err) {
        console.error("SSE parse error:", err);
      }
    };

    eventSource.onerror = () => {
      console.log("EventSource reconnecting...");
    };

    return () => {
      eventSource.close();
    };
  }, []);

  const handleLogOut = () => {
    if (!window.confirm("Are you sure you want to log out?")) return;

    localStorage.removeItem('authToken');
    navigate('/admin-login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <Fragment>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 admin-navbar d-flex justify-content-between align-items-center">
            <div className="navbar-logo-section">
              <button
                className="mobile-menu-toggle"
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
              >
                <FaBars />
              </button>
              <img src={process.env.PUBLIC_URL + '/logo.png'} alt="logo" className="navbar-logo-img" />
            </div>
            <div className="navbar-right-controls">
              {/* Notification Dropdown Container */}
              <div className="notification-dropdown-container" ref={dropdownRef}>
                <button 
                  className="navbar-notification-btn" 
                  aria-label="Notifications"
                  onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                >
                  <FaBell size={20} />
                  {unreadCount > 0 && (
                    <span className="navbar-notification-badge">{unreadCount}</span>
                  )}
                </button>

                {notifDropdownOpen && (
                  <div className="notification-dropdown shadow-lg">
                    <div className="dropdown-header d-flex justify-content-between align-items-center">
                      <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>Notifications</span>
                      {unreadCount > 0 && (
                        <button className="btn-mark-all-read btn btn-link text-decoration-none p-0" onClick={markAllRead}>
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="dropdown-body scrollbar-custom">
                      {notifications.length > 0 ? (
                        notifications.slice(0, 5).map((notif) => (
                          <div 
                            key={notif._id} 
                            className={`dropdown-item-notif d-flex align-items-start gap-2 ${!notif.read ? 'unread' : ''}`}
                            onClick={() => handleNotificationClick(notif)}
                          >
                            <div className={`notif-icon-circle ${notif.type}`}>
                              {notif.type === 'new' ? <FaShoppingBag size={11} /> : notif.type === 'cancelled' ? <FaTimesCircle size={11} /> : <FaCheckCircle size={11} />}
                            </div>
                            <div className="notif-content flex-grow-1">
                              <div className="d-flex justify-content-between align-items-baseline">
                                <span className="notif-title">{notif.title}</span>
                                <span className="notif-time">{formatTimeShort(notif.createdAt)}</span>
                              </div>
                              <p className="notif-message">{notif.message}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-muted">
                          <FaBell className="mb-2" size={20} style={{ color: '#cbd5e1' }} />
                          <p className="mb-0 small">No notifications</p>
                        </div>
                      )}
                    </div>
                    <div className="dropdown-footer text-center">
                      <button 
                        className="btn btn-link text-decoration-none w-100 py-1 text-danger fw-bold" 
                        onClick={() => {
                          setNotifDropdownOpen(false);
                          navigate('/admin/notifications');
                        }}
                        style={{ fontSize: '0.8rem' }}
                      >
                        View All Activity
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <style>{`
                .notification-dropdown-container {
                  position: relative;
                  display: inline-block;
                }
                .notification-dropdown {
                  position: absolute;
                  top: 42px;
                  right: 0;
                  width: 320px;
                  background: #ffffff;
                  border: 1px solid #cbd5e1;
                  border-radius: 0;
                  z-index: 1050;
                  text-align: left;
                }
                .dropdown-header {
                  padding: 10px 14px;
                  border-bottom: 1px solid #cbd5e1;
                }
                .btn-mark-all-read {
                  font-size: 0.78rem;
                  color: #ed3237;
                  font-weight: 600;
                }
                .btn-mark-all-read:hover {
                  color: #c92429;
                }
                .dropdown-body {
                  max-height: 300px;
                  overflow-y: auto;
                }
                .dropdown-item-notif {
                  padding: 12px 14px;
                  border-bottom: 1px solid #cbd5e1;
                  cursor: pointer;
                  transition: background 0.15s ease;
                }
                .dropdown-item-notif:hover {
                  background: #f8fafc;
                }
                .dropdown-item-notif.unread {
                  background: #f0f9ff;
                  border-left: 3px solid #3b82f6;
                }
                .notif-icon-circle {
                  width: 26px;
                  height: 26px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                }
                .notif-icon-circle.new {
                  background: #dbeafe;
                  color: #2563eb;
                }
                .notif-icon-circle.cancelled {
                  background: #fee2e2;
                  color: #dc2626;
                }
                .notif-icon-circle.update {
                  background: #dcfce7;
                  color: #16a34a;
                }
                .notif-title {
                  font-size: 0.82rem;
                  font-weight: 700;
                  color: #0f172a;
                }
                .notif-time {
                  font-size: 0.7rem;
                  color: #94a3b8;
                }
                .notif-message {
                  font-size: 0.78rem;
                  color: #64748b;
                  margin: 2px 0 0 0;
                  line-height: 1.3;
                  white-space: normal;
                }
                .dropdown-footer {
                  border-top: 1px solid #cbd5e1;
                  padding: 8px;
                }
                .scrollbar-custom::-webkit-scrollbar {
                  width: 4px;
                }
                .scrollbar-custom::-webkit-scrollbar-thumb {
                  background: #cbd5e1;
                }
              `}</style>

              {/* Merged Admin Profile Dropdown Pill */}
              <div className="dropdown">
                <div 
                  className="navbar-user-profile dropdown-toggle" 
                  id="adminDropdown"
                  data-bs-toggle="dropdown" 
                  aria-expanded="false"
                >
                  <div className="navbar-user-avatar">A</div>
                  <span className="navbar-user-name">Admin</span>
                </div>
                <ul className="dropdown-menu dropdown-menu-end profile-dropdown-menu" aria-labelledby="adminDropdown">
                  <li>
                    <button 
                      className="dropdown-item" 
                      onClick={() => navigate('/admin/settings')}
                    >
                      Settings
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button 
                      className="dropdown-item text-danger" 
                      onClick={handleLogOut}
                    >
                      <FaSignOutAlt className="me-1" /> Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <AdminSidebar isOpen={sidebarOpen} onItemClick={closeSidebar} />
          <div className="col-12 admin-content">
            <Outlet />
          </div>
        </div>
      </div>
      
      {/* Mobile overlay */}
      <div 
        className={`mobile-overlay ${sidebarOpen ? 'active' : ''}`} 
        onClick={closeSidebar}
      ></div>
      <ToastContainer />
    </Fragment>
  );
}

export default LandingPageAdmin;
